/**
 * @description Displays a customer-facing invoice and its payment/refund details.
 *
 * @dependencies
 * - React Router: Reads invoice and portal-link parameters from the active route.
 * - portalService: Loads, downloads, and pays the customer-scoped invoice.
 * - StatusBadge: Renders customer-friendly invoice status.
 *
 * @notes
 * - Presents the active invoice-link credential when loading details so a copied
 *   invoice-specific link can view its exact unsent invoice.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import portalService from '../../services/portalService';
import { formatCurrency } from '../../utils/amountUtils';
import { formatDate } from '../../utils/dateUtils';
import StatusBadge from '../shared/StatusBadge';

function PortalInvoiceDetails() {
  const { invoiceNumber, shortCode, token } = useParams();
  const context = useOutletContext();
  const publicId = context?.customerPublicId || context?.customerInfo?.customer?.publicId || context?.portalData?.customerPublicId;
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [transactionUpdatesExpanded, setTransactionUpdatesExpanded] = useState(false);
  
  console.log('PortalInvoiceDetails context:', context);
  console.log('PublicId:', publicId);
  console.log('InvoiceNumber:', invoiceNumber);

  const fetchInvoiceDetails = useCallback(async () => {
    // If we don't have publicId yet but have invoiceNumber, wait for context to load
    if (!invoiceNumber) return;
    
    // If publicId is not available, we might be in a loading state
    if (!publicId) {
      console.log('Waiting for publicId from context...');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching invoice details:', { publicId, invoiceNumber });
      const response = await portalService.getInvoice(publicId, invoiceNumber, { shortCode, token });
      setInvoice(response);
      
      // Fetch refunds for this invoice
      setRefundsLoading(true);
      try {
        const refundsResponse = await portalService.getInvoiceRefunds(publicId, invoiceNumber);
        setRefunds(refundsResponse || []);
      } catch (refundError) {
        console.error('Error fetching refunds:', refundError);
        // Don't fail the whole page if refunds fail to load
        setRefunds([]);
      } finally {
        setRefundsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  }, [publicId, invoiceNumber, shortCode, token]);

  // Try to fetch again when publicId becomes available
  useEffect(() => {
    fetchInvoiceDetails();
  }, [fetchInvoiceDetails]);

  // formatCurrency utility now imported from amountUtils
  // formatDate utility now imported from dateUtils (timezone-safe)


  const handlePay = () => {
    // Navigate to payment page with this single invoice
    navigate(`../pay`, {
      state: { 
        selectedInvoices: [invoice.id],
        publicId: publicId,
        customerInfo: context?.customerInfo,
        portalData: context?.portalData,
        fromUserAction: true  // Flag to prevent auto-redirect interference
      }
    });
  };

  const handleDownload = async () => {
    try {
      const blob = await portalService.downloadInvoice(publicId, invoice.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Invoice not found'}</p>
        <button
          onClick={() => navigate('../invoices')}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('../invoices')}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Invoices
        </button>
        <StatusBadge 
          type="invoice_status" 
          value={{
            status: invoice?.status,
            paymentStatus: invoice?.paymentStatus,
            dueDate: invoice?.dueDate,
            displayStatus: invoice?.displayStatus
          }}
          size="sm"
        />
      </div>

      {/* Transaction Updates Card */}
      {refunds.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
            <button
              onClick={() => setTransactionUpdatesExpanded(!transactionUpdatesExpanded)}
              className="w-full flex items-center justify-between text-left"
            >
              <h2 className="text-lg font-semibold text-gray-900">Transaction Updates</h2>
              <svg 
                className={`w-5 h-5 text-gray-600 transition-transform ${transactionUpdatesExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          {transactionUpdatesExpanded && (
            <div className="p-6">
              {refundsLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {refunds.map((refund) => (
                    <div key={refund.id} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Refund Processed</p>
                          <p className="text-sm text-gray-600">{formatDate(refund.date)}</p>
                          {refund.reason && refund.reason !== 'Refund processed' && (
                            <p className="text-sm text-gray-600 mt-1">Reason: {refund.reason}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-orange-600">
                            -{formatCurrency(refund.amount)}
                          </p>
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            {refund.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Invoice Details Card */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Invoice Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
              <p className="text-sm text-gray-600 mt-1">
                Issued on {formatDate(invoice.invoiceDate)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Amount Due</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(invoice.amountDue)}
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Body */}
        <div className="p-6 space-y-6">
          {/* Merchant and Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* From */}
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">From</h3>
              <p className="font-semibold text-gray-900">{invoice.merchantName}</p>
              {invoice.merchantDisplayAddress && (
                <p className="text-sm text-gray-600">{invoice.merchantDisplayAddress}</p>
              )}
              {(invoice.merchantDisplayCity || invoice.merchantDisplayState || invoice.merchantDisplayPostalCode) && (
                <p className="text-sm text-gray-600">
                  {[invoice.merchantDisplayCity, invoice.merchantDisplayState, invoice.merchantDisplayPostalCode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              {invoice.merchantDisplayEmail && (
                <p className="text-sm text-gray-600">{invoice.merchantDisplayEmail}</p>
              )}
              {invoice.merchantDisplayPhone && (
                <p className="text-sm text-gray-600">{invoice.merchantDisplayPhone}</p>
              )}
              {invoice.merchantDisplayWebsite && (
                <p className="text-sm text-gray-600">{invoice.merchantDisplayWebsite}</p>
              )}
            </div>

            {/* To — per-invoice bill_to_* snapshot (LYN-139); never live CRM address */}
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">To</h3>
              <p className="font-semibold text-gray-900">
                {invoice.billToCompany
                  || invoice.billToName
                  || '—'}
              </p>
              {invoice.billToEmail && (
                <p className="text-sm text-gray-600">{invoice.billToEmail}</p>
              )}
              {(invoice.billToAddress || invoice.billToAddressLine1 || invoice.billToAddressLine2
                || invoice.billToCity || invoice.billToCountry) ? (
                <div className="text-sm text-gray-600 mt-1">
                  {(invoice.billToAddressLine1 || invoice.billToAddress) && (
                    <p>{invoice.billToAddressLine1 || invoice.billToAddress}</p>
                  )}
                  {invoice.billToAddressLine2 && (
                    <p>{invoice.billToAddressLine2}</p>
                  )}
                  {(invoice.billToCity || invoice.billToState || invoice.billToPostalCode) && (
                    <p>
                      {[invoice.billToCity, invoice.billToState].filter(Boolean).join(', ')}
                      {invoice.billToPostalCode ? ` ${invoice.billToPostalCode}` : ''}
                    </p>
                  )}
                  {invoice.billToCountry && (
                    <p>{invoice.billToCountry}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic mt-1">No billing address on this invoice</p>
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Invoice Date</p>
                <p className="font-medium">{formatDate(invoice.invoiceDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Due Date</p>
                <p className="font-medium">{formatDate(invoice.dueDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Terms</p>
                <p className="font-medium">
                  {invoice.paymentTerms?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
              {invoice.poNumber && (
                <div>
                  <p className="text-sm text-gray-600">PO Number</p>
                  <p className="font-medium">{invoice.poNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Customer Notes */}
          {invoice.customerNotes && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Notes</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.customerNotes}</p>
            </div>
          )}

          {/* Line Items */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-sm font-medium text-gray-600 pb-2">Description</th>
                    <th className="text-right text-sm font-medium text-gray-600 pb-2">Qty</th>
                    <th className="text-right text-sm font-medium text-gray-600 pb-2">Rate</th>
                    <th className="text-right text-sm font-medium text-gray-600 pb-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3">
                        <p className="font-medium text-gray-900">{item.itemName}</p>
                        {item.description && (
                          <p className="text-sm text-gray-600">{item.description}</p>
                        )}
                      </td>
                      <td className="text-right py-3 text-gray-700">{item.quantity}</td>
                      <td className="text-right py-3 text-gray-700">{formatCurrency(item.unitPrice)}</td>
                      <td className="text-right py-3 font-medium text-gray-900">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
              </div>
              {(invoice.appliedDiscounts || []).length > 0
                ? invoice.appliedDiscounts.map((row, index) => {
                  const amount = Number(row.amountCents ?? row.amount_cents ?? 0) / 100;
                  if (amount <= 0) return null;
                  return (
                    <div key={`named-discount-${index}`} className="flex justify-between text-sm">
                      <span className="text-gray-600">{row.title || 'Discount'}</span>
                      <span className="font-medium text-green-600">-{formatCurrency(amount)}</span>
                    </div>
                  );
                })
                : ((invoice.discountAmount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount</span>
                    <span className="font-medium text-green-600">-{formatCurrency(invoice.discountAmount)}</span>
                  </div>
                ))}
              {invoice.taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax ({invoice.taxRate}%)</span>
                  <span className="font-medium">{formatCurrency(invoice.taxAmount)}</span>
                </div>
              )}
              {invoice.shippingCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">{formatCurrency(invoice.shippingCost)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                <span>Total</span>
                <span>{formatCurrency(invoice.totalAmount)}</span>
              </div>
              {/* Dynamic payment breakdown - shows credit and payments separately when both exist */}
              {invoice.amountPaid > 0 && (
                <>
                  {/* If both credit and regular payments exist, show them separately */}
                  {invoice.creditAmountCents > 0 && invoice.paymentAmountCents > 0 ? (
                    <>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Credit Applied</span>
                        <span className="font-medium">-{formatCurrency(invoice.creditAmount)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Payment Received</span>
                        <span className="font-medium">-{formatCurrency(invoice.paymentAmount)}</span>
                      </div>
                    </>
                  ) : invoice.creditAmountCents > 0 ? (
                    /* Only credit applied */
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Credit Applied</span>
                      <span className="font-medium">-{formatCurrency(invoice.creditAmount)}</span>
                    </div>
                  ) : (
                    /* Only regular payment(s) */
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Amount Paid</span>
                      <span className="font-medium">-{formatCurrency(invoice.amountPaid)}</span>
                    </div>
                  )}
                  {invoice.amountDue > 0 && (
                    <div className="flex justify-between text-lg font-semibold text-red-600">
                      <span>Amount Due</span>
                      <span>{formatCurrency(invoice.amountDue)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Terms & Footer */}
          {(invoice.terms || invoice.footer) && (
            <div className="border-t border-gray-200 pt-6 space-y-4">
              {invoice.terms && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Terms & Conditions</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.terms}</p>
                </div>
              )}
              {invoice.footer && (
                <div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.footer}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Invoice
            </button>
            {invoice.canPay && (
              <button
                onClick={handlePay}
                className="px-6 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
              >
                Pay {formatCurrency(invoice.amountDue)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortalInvoiceDetails;